/** Utilidades de geometría en coordenadas de mundo. Todas puras: no mutan sus argumentos. */

export interface Point {
  x: number
  y: number
}

export interface Box {
  x: number
  y: number
  w: number
  h: number
}

export const clamp = (n: number, min: number, max: number): number => Math.min(max, Math.max(min, n))

export const lerp = (a: number, b: number, t: number): number => a + (b - a) * t

export function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(bx - ax, by - ay)
}

/** Distancia de un punto al segmento AB. */
export function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax
  const dy = by - ay
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) return Math.hypot(px - ax, py - ay)
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq
  t = clamp(t, 0, 1)
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy))
}

export function rotatePoint(px: number, py: number, cx: number, cy: number, angle: number): Point {
  if (!angle) return { x: px, y: py }
  const s = Math.sin(angle)
  const c = Math.cos(angle)
  const dx = px - cx
  const dy = py - cy
  return { x: cx + dx * c - dy * s, y: cy + dx * s + dy * c }
}

export function boxCenter(b: Box): Point {
  return { x: b.x + b.w / 2, y: b.y + b.h / 2 }
}

export function boxesIntersect(a: Box, b: Box): boolean {
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y)
}

export function boxContains(outer: Box, inner: Box): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  )
}

export function pointInBox(px: number, py: number, b: Box, pad = 0): boolean {
  return px >= b.x - pad && px <= b.x + b.w + pad && py >= b.y - pad && py <= b.y + b.h + pad
}

export function unionBoxes(boxes: Box[]): Box | null {
  if (boxes.length === 0) return null
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const b of boxes) {
    minX = Math.min(minX, b.x)
    minY = Math.min(minY, b.y)
    maxX = Math.max(maxX, b.x + b.w)
    maxY = Math.max(maxY, b.y + b.h)
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

export function expandBox(b: Box, pad: number): Box {
  return { x: b.x - pad, y: b.y - pad, w: b.w + pad * 2, h: b.h + pad * 2 }
}

/** Los 4 vértices de una caja rotada, en orden TL, TR, BR, BL. */
export function boxCorners(b: Box, rotation: number): Point[] {
  const c = boxCenter(b)
  return [
    rotatePoint(b.x, b.y, c.x, c.y, rotation),
    rotatePoint(b.x + b.w, b.y, c.x, c.y, rotation),
    rotatePoint(b.x + b.w, b.y + b.h, c.x, c.y, rotation),
    rotatePoint(b.x, b.y + b.h, c.x, c.y, rotation),
  ]
}

/** Bounding box alineado a los ejes de una caja rotada. */
export function rotatedBounds(b: Box, rotation: number): Box {
  if (!rotation) return b
  const pts = boxCorners(b, rotation)
  const xs = pts.map((p) => p.x)
  const ys = pts.map((p) => p.y)
  const minX = Math.min(...xs)
  const minY = Math.min(...ys)
  return { x: minX, y: minY, w: Math.max(...xs) - minX, h: Math.max(...ys) - minY }
}

/** ¿El segmento AB cruza la caja? (usado por el borrador de trazo y el lazo) */
export function segmentIntersectsBox(ax: number, ay: number, bx: number, by: number, box: Box): boolean {
  if (pointInBox(ax, ay, box) || pointInBox(bx, by, box)) return true
  const { x, y, w, h } = box
  return (
    segmentsIntersect(ax, ay, bx, by, x, y, x + w, y) ||
    segmentsIntersect(ax, ay, bx, by, x + w, y, x + w, y + h) ||
    segmentsIntersect(ax, ay, bx, by, x + w, y + h, x, y + h) ||
    segmentsIntersect(ax, ay, bx, by, x, y + h, x, y)
  )
}

export function segmentsIntersect(
  x1: number, y1: number, x2: number, y2: number,
  x3: number, y3: number, x4: number, y4: number,
): boolean {
  const d = (x2 - x1) * (y4 - y3) - (y2 - y1) * (x4 - x3)
  if (d === 0) return false
  const u = ((x3 - x1) * (y4 - y3) - (y3 - y1) * (x4 - x3)) / d
  const v = ((x3 - x1) * (y2 - y1) - (y3 - y1) * (x2 - x1)) / d
  return u >= 0 && u <= 1 && v >= 0 && v <= 1
}

/** Punto dentro de un polígono (regla par-impar). Para el lazo de selección. */
export function pointInPolygon(px: number, py: number, poly: number[]): boolean {
  let inside = false
  const n = poly.length / 2
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i * 2]
    const yi = poly[i * 2 + 1]
    const xj = poly[j * 2]
    const yj = poly[j * 2 + 1]
    const intersect = yi > py !== yj > py && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi
    if (intersect) inside = !inside
  }
  return inside
}

export function polygonBounds(poly: number[]): Box {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (let i = 0; i < poly.length; i += 2) {
    minX = Math.min(minX, poly[i])
    maxX = Math.max(maxX, poly[i])
    minY = Math.min(minY, poly[i + 1])
    maxY = Math.max(maxY, poly[i + 1])
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
}

/** Redondea a un múltiplo (para el ajuste a cuadrícula). */
export function snapTo(value: number, step: number): number {
  return Math.round(value / step) * step
}

export function shortAngle(angle: number): number {
  const twoPi = Math.PI * 2
  return ((angle % twoPi) + twoPi) % twoPi
}
